import type { Dispatch, SetStateAction } from 'react';
import { memo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox } from '@/components/Checkbox';
import { engravingMessages } from '@/messages/engraving';

import type { StyleType } from '../types';

import * as S from './EngravingInputs.styles';

export interface EngravingLettersInputProps {
  isWithSeparators: boolean;
  letters: string[];
  setIsWithSeparators: (withSeparators: boolean) => void;
  setLetters: Dispatch<SetStateAction<string[]>>;
  style: StyleType;
}

const EngravingLettersInputComponent = ({
  isWithSeparators,
  letters,
  setIsWithSeparators,
  setLetters,
  style,
}: EngravingLettersInputProps) => {
  const { formatMessage } = useIntl();

  const sanitizeLetter = (value: string): string => {
    const alnumOnly = value.replaceAll(/[^A-Za-z]/g, '');
    return alnumOnly.slice(0, 1);
  };

  const inputsRef = useRef<Array<HTMLInputElement | null>>([null, null, null]);

  const handleLetterChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = sanitizeLetter(e.target.value);
      setLetters((prev) => {
        const next = [...prev];
        next[index] = sanitized;
        return next;
      });

      if (sanitized && index < inputsRef.current.length - 1) {
        setTimeout(() => {
          const nextInput = inputsRef.current[index + 1];
          nextInput?.focus();
        }, 0);
      }
    };

  const handleLetterBeforeInput =
    (index: number) => (e: React.FormEvent<HTMLInputElement>) => {
      const native = e.nativeEvent as unknown as InputEvent;
      const data = native?.data ?? '';

      if (!data) return;

      if (!/^[A-Za-z]$/.test(data)) {
        e.preventDefault();
        return;
      }

      e.preventDefault();
      setLetters((prev) => {
        const next = [...prev];
        next[index] = data;
        return next;
      });

      if (index < inputsRef.current.length - 1) {
        setTimeout(() => {
          const nextInput = inputsRef.current[index + 1];
          nextInput?.focus();
        }, 0);
      } else {
        setTimeout(() => {
          const current = inputsRef.current[index];
          current?.focus();
        }, 0);
      }
    };

  const handleLetterKeyDown =
    (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        const currentVal = letters[index] ?? '';
        if (currentVal === '' && index > 0) {
          e.preventDefault();
          setLetters((prev) => {
            const next = [...prev];
            const prevIndex = index - 1;
            next[prevIndex] = '';
            return next;
          });
          setTimeout(() => {
            inputsRef.current[index - 1]?.focus();
          }, 0);
        }
      }
    };

  const handlePaste =
    (startIndex: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      const text = e.clipboardData.getData('text') || '';
      const lettersOnly = text.replace(/[^A-Za-z]/g, '');
      if (!lettersOnly) return;
      e.preventDefault();
      setLetters((prev) => {
        const next = [...prev];
        let cursor = startIndex;
        for (
          let i = 0;
          i < lettersOnly.length && cursor < inputsRef.current.length;
          i += 1
        ) {
          next[cursor] = lettersOnly[i];
          cursor += 1;
        }
        return next;
      });
      setTimeout(() => {
        const endIndex = Math.min(
          startIndex + lettersOnly.length - 1,
          inputsRef.current.length - 1,
        );
        inputsRef.current[endIndex]?.focus();
      }, 0);
    };

  return (
    <>
      <S.Wrapper>
        <S.CharacterBox $withSeparater={isWithSeparators}>
          <S.CharacterInput
            ref={(el) => {
              inputsRef.current[0] = el;
            }}
            $style={style}
            autoCapitalize='none'
            autoComplete='off'
            inputMode='text'
            maxLength={1}
            onBeforeInput={handleLetterBeforeInput(0)}
            onChange={handleLetterChange(0)}
            onKeyDown={handleLetterKeyDown(0)}
            onPaste={handlePaste(0)}
            pattern='[A-Za-z]'
            value={letters[0]}
          />
        </S.CharacterBox>

        <S.CharacterBox $withSeparater={isWithSeparators}>
          <S.CharacterInput
            ref={(el) => {
              inputsRef.current[1] = el;
            }}
            $style={style}
            autoCapitalize='none'
            autoComplete='off'
            inputMode='text'
            maxLength={1}
            onBeforeInput={handleLetterBeforeInput(1)}
            onChange={handleLetterChange(1)}
            onKeyDown={handleLetterKeyDown(1)}
            onPaste={handlePaste(1)}
            pattern='[A-Za-z]'
            value={letters[1]}
          />
        </S.CharacterBox>

        <S.CharacterBox>
          <S.CharacterInput
            ref={(el) => {
              inputsRef.current[2] = el;
            }}
            $style={style}
            autoCapitalize='none'
            autoComplete='off'
            inputMode='text'
            maxLength={1}
            onBeforeInput={handleLetterBeforeInput(2)}
            onChange={handleLetterChange(2)}
            onKeyDown={handleLetterKeyDown(2)}
            onPaste={handlePaste(2)}
            pattern='[A-Za-z]'
            value={letters[2]}
          />
        </S.CharacterBox>
      </S.Wrapper>

      <S.Wrapper>
        <Checkbox
          checked={isWithSeparators}
          onChange={(e) => setIsWithSeparators(e.target.checked)}
        >
          {formatMessage(engravingMessages.withSeparators)}
        </Checkbox>
      </S.Wrapper>
    </>
  );
};

export const EngravingLettersInput = memo(EngravingLettersInputComponent);
